import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Define the shape of our cache state dictionary
interface ImageCache {
  [url: string]: string; // Maps original URL string to Base64 string
}

// Define the Context value structure
interface ImageCacheContextType {
  cache: ImageCache;
  loadImage: (url: string) => Promise<string>;
}

const ImageCacheContext = createContext<ImageCacheContextType | undefined>(undefined);

// Helper function to fetch and convert image to Base64
const convertToBase64 = (url: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(blob);
      })
      .catch((err) => reject(err));
  });
};

interface ImageCacheProviderProps {
  children: ReactNode;
}

export function ImageCacheProvider({ children }: ImageCacheProviderProps) {
  // Purely memory-bound React state
  const [cache, setCache] = useState<ImageCache>({});

  const loadImage = useCallback(async (url: string): Promise<string> => {
    if (!url) return '';

    // Serve directly from memory if it already exists
    if (cache[url]) return cache[url];

    try {
      const base64Data = await convertToBase64(url);
      setCache((prev) => ({ ...prev, [url]: base64Data }));
      return base64Data;
    } catch (error) {
      console.error('Failed to cache image in-memory:', error);
      return url; // Fallback to original network URL
    }
  }, [cache]);

  return (
    <ImageCacheContext.Provider value={{ cache, loadImage }}>
      {children}
    </ImageCacheContext.Provider>
  );
}

export function useImageCache(): ImageCacheContextType {
  const context = useContext(ImageCacheContext);
  if (context === undefined) {
    throw new Error('useImageCache must be used within an ImageCacheProvider');
  }
  return context;
}
